/**
 * Pure helpers for applying a voice-parse result to the modal's form. Split
 * out so the Remarks append rule and the additive tag merge can be unit-
 * tested in isolation — the modal integration just composes them.
 */

/**
 * The client-side Remarks append rule (per US-012):
 *
 * - Empty existing → return the server block verbatim.
 * - Non-empty existing → `existing.trimEnd() + "\n\n" + serverBlock`.
 *
 * The server-provided block already carries its `[YYYY-MM-DD HH:MM SGT]`
 * header — the client adds nothing. This is uniform across create and edit
 * modes; "always append, never replace" emerges naturally.
 */
export function appendVoiceRemarks(
  existing: string | null | undefined,
  serverBlock: string,
): string {
  const safeExisting = (existing ?? "").toString();
  if (safeExisting.trim() === "") return serverBlock;
  return `${safeExisting.trimEnd()}\n\n${serverBlock}`;
}

/**
 * Compute the character range of the AI-sourced block within the merged
 * remarks string. Mirrors `appendVoiceRemarks`'s join logic so the modal
 * can highlight the inserted slice without re-deriving offsets.
 */
export function aiRemarksRangeOf(
  existing: string | null | undefined,
  serverBlock: string,
): { start: number; end: number } {
  const safeExisting = (existing ?? "").toString();
  if (safeExisting.trim() === "") {
    return { start: 0, end: serverBlock.length };
  }
  const prefix = `${safeExisting.trimEnd()}\n\n`;
  return { start: prefix.length, end: prefix.length + serverBlock.length };
}

export interface TagLike {
  id: string;
  name: string;
}

export interface MergeVoiceTagsResult {
  /** Full merged id array (or original `selectedIds` reference when nothing
   * was added, so React-Hook-Form does not detect a change). */
  mergedIds: string[];
  /** IDs newly selected by this voice call. Empty if no project tag matched
   * or all matches were already selected. */
  aiSelectedTagIds: string[];
  /** AI-returned names that did not match any project tag (case-insensitive).
   * Today: silently dropped; preserved here for future surfacing. */
  unmatchedTagNames: string[];
}

/**
 * Additive case-insensitive merge of voice-matched tag names into the
 * current selection. Existing selections are never removed; duplicates
 * collapse. Returns metadata distinguishing AI-newly-selected IDs from
 * names with no project-tag match.
 */
export function mergeVoiceTags(
  selectedIds: string[],
  tagNames: string[],
  availableTags: TagLike[],
): MergeVoiceTagsResult {
  if (tagNames.length === 0) {
    return {
      mergedIds: selectedIds,
      aiSelectedTagIds: [],
      unmatchedTagNames: [],
    };
  }

  const byLowerName = new Map<string, string>();
  for (const tag of availableTags) {
    byLowerName.set(tag.name.toLowerCase(), tag.id);
  }

  const existingSet = new Set(selectedIds);
  const next = new Set(selectedIds);
  const aiSelectedTagIds: string[] = [];
  const unmatchedTagNames: string[] = [];

  for (const name of tagNames) {
    const id = byLowerName.get(name.toLowerCase());
    if (!id) {
      unmatchedTagNames.push(name);
      continue;
    }
    if (existingSet.has(id)) continue;
    if (next.has(id)) continue; // dedupe within voice call
    next.add(id);
    aiSelectedTagIds.push(id);
  }

  const mergedIds =
    aiSelectedTagIds.length === 0 ? selectedIds : Array.from(next);
  return { mergedIds, aiSelectedTagIds, unmatchedTagNames };
}
